import { useStore } from '@sar/framework/hooks'
import { useLocation, useMatch, useParams, useNavigate } from 'react-router'

import { Layout } from 'antd'
import { ModalForm, ProFormText } from '@ant-design/pro-components'

import { Navigate } from 'react-router-dom'
import { Logo, Menu, Header, Footer, Bookmark } from 'components'

function getMenu (data) {
  const menu = R.evolve({
      icon: e => e ? <i className={`fa fa-${e}`}/> : null
    , children: e => e?.filter(({ type }) => type < 3).filter(R.prop('path')).map(getMenu)
  })(
    data
  )

  if (R.isNil(menu.icon)) {
    delete menu.icon
  }

  if (R.isEmpty(menu.children)) {
    delete menu.children
  }

  else if (menu.type > 2) {
    return null
  }

  return R.pick([ 'key', 'label', 'icon', 'children' ], { ...menu, key: data.path, label: data.title })
}

export default (Target: React.FC<any>) => function (props: any) {
  let {
      menu
    , app
    , theme
    , refreshConstant } = props

  const store = useStore()
  const location = useLocation()

  const params = useParams()
  const navigate = useNavigate()

  const isolate = R.call(
    R.cond([
      [() => useMatch(`/403`) != null, R.T],
      [() => useMatch(`/404`) != null, R.T],
      [() => useMatch(`/home`) != null, R.T],
      [() => useMatch(`/login`) != null, R.T],
      [() => useMatch(`/nomatch`) != null, R.T],
      [() => useMatch(`/addressing`) != null, R.T],
    ])
  )

  const query = React.useMemo(
    () => {
      return new URLSearchParams(location.search)
    },
    [
      location.search,
    ]
  )

  const menus = React.useMemo(
    () => {
      return R.defaultTo([], menu).map(getMenu).filter(R.identity).filter(R.prop('key'))
    },
    [
      menu,
    ]
  )

  const getMenuByKey = React.useCallback(key => R.flatten([ menus, menus.map(R.prop('children')) ]).filter(R.identity).find(data => {
    if (data.key == key) {
      return true
    }

    else {
      return false
    }
  }), [
    menus,
  ])

  const navigator = React.useMemo(() => {
    return {
      back: (stack = -1) => [ navigate(stack) ],
      to: (url, title = getMenuByKey(url)?.label ?? '临时页') => [ navigate(url), isolate ? null : store.history.add(url, title) ],
      replace: (url, title = getMenuByKey(url)?.label ?? '临时页') => [ navigate(url, { replace: true }), isolate ? null : store.history.add(url, title) ],
    }
  }, [])

  let target = <Target
    app={app}
    searchParams={query}
    store={store}
    menus={menus}
    theme={theme}
    params={params}
    location={location}
    navigator={navigator}
    refreshConstant={refreshConstant}
  />

  if(isolate) {
    return target
  }

  if(store.identity.isLogined() == false){
    return <Navigate replace to='/login'/>
  }

  return <>
    <Layout hasSider style={{ minHeight: '100vh' }}>
      <Layout.Sider width={200}>
        <Logo
          title={store.config.env.title}
          url={store.config.env.prefix ? `${store.config.env.prefix}/images/logo.png` : '/images/logo.png'}
        />

        <Menu
          menus={menus}
          params={params}
          location={location}
          navigator={navigator}
        />
      </Layout.Sider>

      <Layout>
        <Layout.Header style={{ background: `#fff`, boxShadow: `0px 3px 3px rgba(0,0,0,0.1)`, position: 'relative', zIndex: 30 }}>
          <Header
            identity={store.identity}
            onUpdatePassword={() => store.data.dispatch('showUpdatePassword', true)}
            onLogout={() => [
                store.identity.clear()
              , store.history.dispatch([])
            ]}
          />
        </Layout.Header>

        <Layout.Content>
          <Bookmark
            store={store}
            theme={theme}
            navigator={navigator}
            location={location}
          />

          {target}
        </Layout.Content>

        <Layout.Footer>
          <Footer data={store.config.env}/>
        </Layout.Footer>
      </Layout>
    </Layout>

    <ModalForm
      width={500}
      title='修改密码'
      open={store.data.showUpdatePassword}
      modalProps={{ maskClosable: false, destroyOnClose: true }}
      onOpenChange={visible => store.data.dispatch('showUpdatePassword', visible)}
      onFinish={data => store.externals.api.put('/auth/password/my', { oldPassword: store.encrypt.encode(data.oldPassword), password: store.encrypt.encode(data.password) }).then(R.T)}
    >
      <ProFormText.Password
        name='oldPassword'
        label='密码'
        placeholder='您现在的密码'
        rules={[{ required: true, message: '请输入登录密码' } ]}
      />

      <ProFormText.Password
        name='password'
        label='新的密码'
        placeholder='新的登录密码'
        rules={[{ required: true, message: '请确认您的登录密码' }, { min: 6, message: '登录密码必须为6位以上' } ]}
      />

      <ProFormText.Password
        name='repassword'
        label='确认密码'
        placeholder='请确认您的登录密码'
        rules={[{ required: true, message: '请确认您的登录密码' }, ({ getFieldValue }) => ({ validator: (_, value) => R.equals(getFieldValue('password'), value) ? Promise.resolve() : Promise.reject('您输入的密码不一致!') })]}
      />
    </ModalForm>
  </>
}
